/** @format */

import { Component, EventEmitter, Input, Output } from "@angular/core";
import { FormBuilder, FormGroup } from "@angular/forms";
import { BsModalRef, BsModalService } from "ngx-bootstrap/modal";
import { ApiServiceService } from "src/app/Services/api-service.service";
import * as alertify from "alertifyjs";
import { ConfirmDialogComponent } from "src/app/Shared/shared/confirm-dialog/confirm-dialog.component";
import { ErrorDialogComponent } from "src/app/Shared/shared/error-dialog/error-dialog.component";
import { SuccessDialogComponent } from "src/app/Shared/shared/success-dialog/success-dialog.component";
import * as url from "node:url";

@Component({
	selector: "app-edit-user-other-personal-information",
	templateUrl: "./edit-user-other-personal-information.component.html",
	styleUrls: ["./edit-user-other-personal-information.component.css"],
})
export class EditUserOtherPersonalInformationComponent {
	@Output() onConfirmClicked = new EventEmitter();
	@Input() userDetail: any;

	isLoading: boolean = false;
	states: any[] = [];
	lgaList: any[] = [];
	otherInformation: any = null;

	readonly yesNoOptions: string[] = ["YES", "NO"];
	readonly incomeCategories: string[] = [
		"EMPLOYED",
		"SELF EMPLOYED",
		"UNEMPLOYED",
	];
	readonly accommodationTypes: string[] = ["owned", "rented", "leased"];
	readonly buildingTypes: string[] = [
		"bungalow",
		"duplex",
		"flat",
		"self contain",
		"other",
	];
	readonly periodTypes: string[] = ["monthly", "quarterly", "yearly"];
	readonly educationLevels: string[] = [
		"PRIMARY",
		"SECONDARY",
		"OND",
		"HND",
		"BSC",
		"MSC",
		"PHD",
		"OTHER",
	];

	form: FormGroup = this.fb.group({
		incomeCategory: [""],
		employerName: [""],
		incomeRange: [""],
		sourceOfIncome: [""],
		totalAnnualIncome: [""],
		numberOfVehicles: [""],
		natureOfBusiness: [""],
		educationLevel: [""],
		hasVehicle: ["NO"],
		hasProperty: ["NO"],
		propertyType: [""],
		stateOfOrigin: [""],
		lgaOfOrigin: [""],
		landMark: [""],
		accommodationType: [""],
		buildingType: [""],
		rentPeriodCovered: [""],
		periodType: [""],
		rentAmount: [""],
		numberOfRooms: [""],
		landlordFullName: [""],
		landlordPhoneNumber: [""],
		landlordAsin: [""],
		landlordEmail: [""],
		landlordAddress: [""],
		doesLandlordLiveSameApartment: ["NO"],
		hasBusiness: ["NO"],
		ownCompany: ["NO"],
	});

	constructor(
		private apiService: ApiServiceService,
		private bsModalService: BsModalService,
		private fb: FormBuilder,
		private bsModalRef: BsModalRef,
	) {}

	ngOnInit(): void {
		this.getStates();
		this.getAllLga();
		this.fetchOtherInformation();
	}

	getStates() {
		this.apiService.LoginGet("/auth/state/all", {}).subscribe({
			next: (response) => {
				this.states = response.map((state: any) => ({
					...state,
					name: this.toTitleCase(state.name),
				}));
			},
			error: () => {
				alertify.set("notifier", "position", "top-center");
				alertify.error("Unable to load states");
			},
		});
	}

	getAllLga() {
		this.apiService.LoginGet("/auth/lga/all").subscribe({
			next: (response) => {
				this.lgaList = response;
			},
			error: () => {
				alertify.set("notifier", "position", "top-center");
				alertify.error("Unable to load LGAs");
			},
		});
	}

	fetchOtherInformation() {
		this.isLoading = true;
		this.apiService
			.LoginGet(
				`/auth/utility/profile/update?asin=${this.userDetail?.tinNumber}`,
			)
			.subscribe({
				next: (response: any) => {
					this.otherInformation = response?.data ?? null;
					this.patchForm(this.otherInformation);
					this.isLoading = false;
				},
				error: () => {
					this.isLoading = false;
					this.confirmAfterError(
						"Unable to load other personal information",
					);
					alertify.set("notifier", "position", "top-center");
					alertify.error("Error performing this operation");
				},
			});
	}

	patchForm(data: any) {
		if (!data) {
			return;
		}

		this.form.patchValue({
			incomeCategory: data.incomeCategory || "",
			employerName: data.employerName || "",
			incomeRange: data.incomeRange || "",
			sourceOfIncome: data.sourceOfIncome || "",
			totalAnnualIncome: data.totalAnnualIncome || "",
			numberOfVehicles: data.numberOfVehicles || "",
			natureOfBusiness: data.natureOfBusiness || "",
			educationLevel: data.educationLevel || "",
			hasVehicle: this.toYesNo(data.hasVehicle),
			hasProperty: this.toYesNo(data.hasProperty),
			propertyType: this.normalizePropertyType(data.propertyType) || "",
			stateOfOrigin: data.stateOfOrigin || "",
			lgaOfOrigin: data.stateOfOriginLga || "",
			landMark: data.landMark || "",
			accommodationType: data.accommodationType || "",
			buildingType: data.buildingType || "",
			rentPeriodCovered: data.rentPeriodCovered || "",
			periodType: data.periodType || "",
			rentAmount: data.rentAmount || "",
			numberOfRooms: data.numberOfRooms || "",
			landlordFullName: data.landLordName || "",
			landlordPhoneNumber: data.landlordPhoneNumber || "",
			landlordAsin: data.landlordAsin || "",
			landlordEmail: data.landlordEmail || "",
			landlordAddress: data.landlordAddress || "",
			doesLandlordLiveSameApartment: this.toYesNo(
				data.sharedApartmentWithLandLord,
			),
			hasBusiness: this.toYesNo(data.hasBusiness),
			ownCompany: this.toYesNo(data.ownCompany),
		});
	}

	confirmSave() {
		const dfModalRef = this.bsModalService.show(ConfirmDialogComponent, {
			initialState: {
				body: "Proceed to save changes",
			},
			class: "modal-sm modal-dialog-centered",
		});

		dfModalRef?.content?.onConfirmClicked.subscribe(() => {
			this.save();
			dfModalRef.hide();
		});
	}

	save() {
		this.isLoading = true;
		const payload = this.buildPayload();

		console.log("Other personal information payload", payload);
		let url = "/auth/update/other-personal-info";
		this.apiService.post(url, payload).subscribe({
			next: (response) => {
				this.isLoading = false;
				this.confirmAfterSuccess(
					response?.message ||
						"Other personal information updated successfully",
				);
				alertify.set("notifier", "position", "top-center");
				alertify.success(
					response?.message ||
						"Other personal information updated successfully",
				);
				this.onConfirmClicked.emit(payload);
				this.bsModalRef.hide();
			},
			error: (error) => {
				this.isLoading = false;
				console.log("Other personal information update failed", error);
				this.confirmAfterError("Action was not successful");
				alertify.set("notifier", "position", "top-center");
				alertify.error("Error performing this operation");
			},
		});
	}

	buildPayload() {
		const value = this.form.getRawValue();

		return {
			userId: this.userDetail?.userId,
			asin: this.userDetail?.tinNumber,
			incomeCategory: this.emptyToNull(value.incomeCategory),
			employerName: this.emptyToNull(value.employerName),
			incomeRange: this.emptyToNull(value.incomeRange),
			sourceOfIncome: this.emptyToNull(value.sourceOfIncome),
			totalAnnualIncome: this.emptyToNull(value.totalAnnualIncome),
			natureOfBusiness: this.emptyToNull(value.natureOfBusiness),
			educationLevel: this.emptyToNull(value.educationLevel),
			stateOfOrigin: this.emptyToNull(value.stateOfOrigin),
			stateOfOriginLga: this.emptyToNull(value.lgaOfOrigin),
			landMark: this.emptyToNull(value.landMark),
			accommodationType: this.emptyToNull(value.accommodationType),
			buildingType: this.emptyToNull(value.buildingType),
			periodType: this.emptyToNull(value.periodType),
			rentPeriodCovered: this.emptyToNull(value.rentPeriodCovered),
			rentAmount: this.emptyToNull(value.rentAmount),
			numberOfRooms: this.emptyToNull(value.numberOfRooms),
			landLordName: this.emptyToNull(value.landlordFullName),
			landLordPhoneNumber: this.emptyToNull(value.landlordPhoneNumber),
			landLordAsin: this.emptyToNull(value.landlordAsin),
			landLordEmail: this.emptyToNull(value.landlordEmail),
			landlordAddress: this.emptyToNull(value.landlordAddress),
			hasVehicle: this.toBoolean(value.hasVehicle),
			numberOfVehicles: this.emptyToNull(value.numberOfVehicles),
			hasProperty: this.toBoolean(value.hasProperty),
			propertyType: this.normalizePropertyType(value.propertyType),
			sharedApartmentWithLandLord: this.toBoolean(
				value.doesLandlordLiveSameApartment,
			),
			hasBusiness: this.toBoolean(value.hasBusiness),
			ownCompany: this.toBoolean(value.ownCompany),
		};
	}

	isIncomeCategory(category: string): boolean {
		return this.form.get("incomeCategory")?.value === category;
	}

	shouldShowSourceAndIncome(): boolean {
		return ["SELF EMPLOYED", "UNEMPLOYED"].includes(
			this.form.get("incomeCategory")?.value,
		);
	}

	shouldShowBuildingType(): boolean {
		return this.form.get("accommodationType")?.value === "owned";
	}

	shouldShowRentFields(): boolean {
		return ["rented", "leased"].includes(
			this.form.get("accommodationType")?.value,
		);
	}

	shouldShowRoomCount(): boolean {
		return ["bungalow", "duplex", "flat"].includes(
			this.form.get("buildingType")?.value,
		);
	}

	shouldShowLandlordAddress(): boolean {
		return this.form.get("doesLandlordLiveSameApartment")?.value === "NO";
	}

	shouldShowVehicleCount(): boolean {
		return this.form.get("hasVehicle")?.value === "YES";
	}

	shouldShowPropertyType(): boolean {
		return this.form.get("hasProperty")?.value === "YES";
	}

	confirmAfterSuccess(message: any) {
		const dfModalRef = this.bsModalService.show(SuccessDialogComponent, {
			initialState: {
				body: message,
			},
			class: "modal-sm modal-dialog-centered",
		});
		dfModalRef?.content?.onConfirmClicked.subscribe(() => {
			dfModalRef.hide();
		});
	}

	confirmAfterError(message: any) {
		const dfModalRef = this.bsModalService.show(ErrorDialogComponent, {
			initialState: {
				body: message,
			},
			class: "modal-sm modal-dialog-centered",
		});
		dfModalRef?.content?.onConfirmClicked.subscribe(() => {
			dfModalRef.hide();
		});
	}

	close() {
		this.onConfirmClicked.emit();
		this.bsModalRef.hide();
	}

	private emptyToNull(value: any) {
		return value === "" || value === undefined ? null : value;
	}

	private normalizePropertyType(value: any): string | null {
		if (Array.isArray(value)) {
			return value.length ? String(value[0]) : null;
		}

		if (value === "" || value === undefined || value === null) {
			return null;
		}

		return String(value);
	}

	private toYesNo(value: any): string {
		return value ? "YES" : "NO";
	}

	private toBoolean(value: string): boolean {
		return value === "YES";
	}

	private toTitleCase(str: string): string {
		return str
			.toLowerCase()
			.split(" ")
			.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
			.join(" ");
	}
}
